import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { type Location, useLocation, useNavigate } from 'react-router'

import { Logo } from '@/components/brand/Logo'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  googleSignInHref,
  markSignedIn,
  sendSignInCode,
  signInErrorMessage,
  verifySignInCode,
} from '@/models/auth'
import { SignInCodeStep } from './SignInCodeStep'
import { SignInEmailStep } from './SignInEmailStep'

/** A failed Google sign-in lands back on the page it started from with this set to "google". */
const GOOGLE_ERROR_PARAM = 'sign_in_error'
const GOOGLE_FAILED = "We couldn't sign you in with Google. Try again or use your email."

export enum SignInMode {
  SIGN_IN = 'SIGN_IN',
  SIGN_UP = 'SIGN_UP',
}

const HEADINGS: Record<SignInMode, string> = {
  [SignInMode.SIGN_IN]: 'Sign in to Cai',
  [SignInMode.SIGN_UP]: 'Create your Cai account',
}

/** This page without the Google error, so retrying from it can't stack a second one. */
function pathWithoutGoogleError({ pathname, search, hash }: Location): string {
  const params = new URLSearchParams(search)
  params.delete(GOOGLE_ERROR_PARAM)
  const query = params.toString()
  return `${pathname}${query ? `?${query}` : ''}${hash}`
}

/**
 * Email-code and Google sign-in. A code sent to an address with no account creates one,
 * so signing up is this same flow; the mode only changes the heading.
 */
export function SignInPanel({ mode = SignInMode.SIGN_IN }: { mode?: SignInMode }) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const [googleFailed, setGoogleFailed] = useState(
    () => new URLSearchParams(location.search).get(GOOGLE_ERROR_PARAM) === 'google'
  )
  const [email, setEmail] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [codeResent, setCodeResent] = useState(false)

  const sendCode = useMutation({ mutationFn: sendSignInCode })
  const verifyCode = useMutation({
    mutationFn: (code: string) => verifySignInCode(email, code),
    onSuccess: () => markSignedIn(queryClient),
  })

  // Read into state above; off the URL so a reload or a copied link doesn't repeat it.
  useEffect(() => {
    if (new URLSearchParams(location.search).has(GOOGLE_ERROR_PARAM)) {
      navigate(pathWithoutGoogleError(location), { replace: true })
    }
  }, [location, navigate])

  function requestCode(address: string) {
    setGoogleFailed(false)
    setEmail(address)
    sendCode.mutate(address, { onSuccess: () => setCodeSent(true) })
  }

  function resendCode() {
    verifyCode.reset()
    setCodeResent(false)
    sendCode.mutate(email, { onSuccess: () => setCodeResent(true) })
  }

  function verify(code: string) {
    sendCode.reset()
    setCodeResent(false)
    verifyCode.mutate(code)
  }

  function changeEmail() {
    sendCode.reset()
    verifyCode.reset()
    setCodeResent(false)
    setCodeSent(false)
  }

  const failure = verifyCode.error ?? sendCode.error

  return (
    <Card className="w-full max-w-sm">
      <CardHeader className="justify-items-center gap-3 text-center">
        <Logo size="small" width={64} />
        <CardTitle>
          <h1 className="text-ink text-2xl font-bold tracking-[-0.01em]">{HEADINGS[mode]}</h1>
        </CardTitle>
        <CardDescription>
          {codeSent ? (
            <>
              {/* ph-no-capture: input masking covers the field it was typed into, not this copy of it. */}
              Enter the code we sent to <span className="ph-no-capture">{email}</span>.
            </>
          ) : (
            'Continue with Google or your email. New here? Either one creates your account.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {codeSent ? (
          <SignInCodeStep
            isVerifying={verifyCode.isPending || verifyCode.isSuccess}
            isResending={sendCode.isPending}
            errorMessage={failure ? signInErrorMessage(failure) : undefined}
            notice={codeResent ? 'We sent you a new code.' : undefined}
            onSubmit={verify}
            onResend={resendCode}
            onChangeEmail={changeEmail}
          />
        ) : (
          <SignInEmailStep
            initialEmail={email}
            isSending={sendCode.isPending}
            errorMessage={failure ? signInErrorMessage(failure) : googleFailed ? GOOGLE_FAILED : undefined}
            onSubmit={requestCode}
            onGoogle={() => window.location.assign(googleSignInHref(pathWithoutGoogleError(location)))}
          />
        )}
      </CardContent>
    </Card>
  )
}
